import React, { useEffect, useState } from "react";
import { Routes } from "./routes";
import { ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { apiClient } from "./utilities/apiClient";

/**
 * Validates the cookie session against the backend on every load. The admin
 * SPA used to trust `localStorage.user` blindly — but cookies for
 * `api.dresez.com` are shared with the marketplace SPA at `dresez.com`. If
 * the marketplace tab gets focus and refreshes its session, the admin tab's
 * subsequent /auth/refresh call could detect the rotated token as "reuse"
 * and revoke the family. Hit /auth/me on mount — if the response doesn't
 * match a stored admin user, wipe localStorage and redirect to /signin so
 * the admin re-authenticates with a fresh cookie.
 *
 * Note (2026-05-23): admin no longer runs the focus-refresh hook (it caused
 * tab-eviction-on-return on mobile to look like a hard reload, because the
 * marketplace tab's parallel refresh kept revoking the admin family). This
 * mount-time guard is the only thing left — runs once when the admin SPA
 * actually loads.
 */
function useAdminSessionGuard() {
  const [ready, setReady] = useState(false);

  useEffect(() => {
    const stored = (() => {
      try {
        return JSON.parse(window.localStorage.getItem('user') || 'null');
      } catch {
        return null;
      }
    })();

    if (!stored?.id) {
      setReady(true);
      return;
    }

    // Single retry for transient failures (network blip, mid-rotation 401)
    // before we kick the user back to /signin. If both calls fail we honor
    // the original intent — clear localStorage, hard-redirect.
    const fetchMe = () => apiClient.get('/auth/me');

    fetchMe()
      .catch(() => new Promise((r) => setTimeout(r, 500)).then(fetchMe))
      .then((res) => {
        const live = res?.data?.user;
        if (!live?.isAdmin || live.id !== stored.id) {
          window.localStorage.removeItem('user');
          if (window.location.pathname !== '/signin') {
            window.location.href = '/signin';
            return;
          }
        }
      })
      .catch(() => {
        window.localStorage.removeItem('user');
        if (window.location.pathname !== '/signin') {
          window.location.href = '/signin';
          return;
        }
      })
      .finally(() => setReady(true));
  }, []);

  return ready;
}

export default function App() {
  const ready = useAdminSessionGuard();
  if (!ready) return null;
  return (
    <>
      <Routes />
      <ToastContainer />
    </>
  );
}
